import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { AlulaReceiver } from "@app/project/model/alula-receiver";
import { CentralStation } from "@app/project/model/alula-receiver-group";
import { AlulaReceiverService } from "@app/project/services/alula-receiver.service";
import { AlulaDispatchers } from "@app/project/store/dispatchers/alula.dispatchers";
import { SnackbarActions } from "@app/root-store/actions";
import { ClareDialogComponent } from "@app/shared/components/clare-dialog/clare-dialog.component";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { AppErrorStateMatcher } from "@app/shared/validators/app-error-state-matcher.service";
import { usPhoneValid } from "@app/shared/validators/phoneValid";
import { BehaviorSubject } from "rxjs";
import { take, tap } from "rxjs/operators";
import { AppHelperService } from "../../../../../shared/services/app-helper.service";
import { AlulaReceiverInputComponent } from "../alula-receiver-input/alula-receiver-input.component";
import { ReceiverTypes } from "../central-station.model";

@Component({
    selector: "app-alula-receivers-group-add",
    templateUrl: "./alula-receivers-group-add.component.html",
    styleUrls: ["./alula-receivers-group-add.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlulaReceiversGroupAddComponent implements OnInit, OnChanges {
    @ViewChild("commPathTmpl") commPathTmpl: TemplateRef<AlulaReceiverInputComponent>;
    @Output() onSave = new EventEmitter<any>();
    @Output() onCancel = new EventEmitter<void>();
    @Output() onShowReceiverAccounts = new EventEmitter<string>();
    @Output() onReceiverUpdate = new EventEmitter<AlulaReceiver[]>();
    @Output() onRemoveReceiverUpdate = new EventEmitter<AlulaReceiver[]>();
    @Input() centralStation: CentralStation;
    @Input() dealerId: string;
    @Input() initRemovals: AlulaReceiver[];
    validObj: { hasValidReceivers: boolean; hasInvalidReceivers: boolean };

    editingCommIndex: number = null;
    editingReceiver: AlulaReceiver = null;

    public submitAttempted = false;
    public phoneReceiverForm: FormGroup = this.formBuilder.group({
        id: [],
        name: [""],
        receiverType: [ReceiverTypes.pstn],
        path: ["", usPhoneValid],
        description: ["Standard Phone Line"],
        dealerId: [""],
    });
    public receiverGroupForm: FormGroup = this.formBuilder.group({
        id: [],
        dealerId: [""],
        name: ["", Validators.required],
    });
    public pendingRemoval: Record<number, AlulaReceiver> = {};

    constructor(
        private readonly formBuilder: FormBuilder,
        public readonly errorMatcher: AppErrorStateMatcher,
        private readonly dialog: MatDialog,
        private readonly alulaDispatcher: AlulaDispatchers,
        private cdr: ChangeDetectorRef,
        private readonly alulaReceiverService: AlulaReceiverService
    ) {}

    get phoneReceiverFields() {
        return this.phoneReceiverForm.controls;
    }

    get receiverGroupFields() {
        return this.receiverGroupForm.controls;
    }
    get pendingRemovalValues() {
        return Object.values(this.pendingRemoval);
    }

    checkAllFormsValid(centralStation: CentralStation) {
        return (
            this.receiverGroupForm.valid &&
            centralStation.receivers.some(receiver => !!receiver) &&
            (!this.phoneReceiverForm.controls.path.value || this.phoneReceiverForm.valid)
        );
    }

    ngOnInit(): void {
        this.initCentralStation();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.centralStation?.currentValue && !changes.centralStation.previousValue) {
            this.initCentralStation();
        }
    }

    initCentralStation() {
        if (this.initRemovals?.length) {
            this.initRemovals.forEach(removal => {
                const index = this.centralStation?.receivers?.findIndex(rcvr => rcvr?.id === removal.id);
                this.pendingRemoval[index] = removal;
            });
        }
        this.validObj = this.alulaReceiverService.doesHaveValidReceivers(this.centralStation);
        if (this.centralStation?.receivers?.[2] && !this.pendingRemoval[2]) {
            this.phoneReceiverForm.patchValue(this.centralStation.receivers?.[2], { emitEvent: true });
        } else {
            this.phoneReceiverFields.dealerId.setValue(this.dealerId);
        }

        this.receiverGroupForm.patchValue({ ...this.centralStation, dealerId: this.dealerId });
    }

    showInvalidCentralStationError() {
        const saveNewStationMsg =
            "Cannot save Central Station without a name and at least one Communication Path or Phone Number. Please complete the form.";
        this.alulaDispatcher.dispatchAction(
            SnackbarActions.openSnackbar({
                message: new HttpResponseHandler(null, null, saveNewStationMsg, true),
            })
        );
    }
    showRemoveReceiverMsg(index: 0 | 1) {
        const receiverType = ["Communication Path", "Phone Line"];
        const config: MatDialogConfig = {
            id: "delete-receiver",
            maxWidth: "475px",
            data: {
                title: `Delete ${receiverType[index]}`,
                message: `This ${receiverType[index]} has been set 
                for deletion. Please save the central station in order to complete the removal process.`,
                buttons: [
                    {
                        text: "Ok",
                        method: "close",
                    },
                ],
            },
        };

        this.dialog.open(ClareDialogComponent, config);
    }

    saveCentralStation() {
        this.submitAttempted = true;
        const centralStation = { ...this.centralStation, ...this.receiverGroupForm.value };
        centralStation.receivers = [...centralStation.receivers];
        if (this.phoneReceiverForm.valid) {
            if (this.phoneReceiverForm.value.path || centralStation.receivers[2]) {
                centralStation.receivers[2] = this.phoneReceiverForm.value;
            } else {
                centralStation.receivers[2] = null;
            }
        }
        if (!this.checkAllFormsValid(centralStation)) {
            this.submitAttempted = false;
            return this.showInvalidCentralStationError();
        }

        const saveCallCount = new BehaviorSubject(0);
        const callback = new EventEmitter();
        this.onSave.emit({ centralStation, saveCallCount, callback });
        callback.pipe(take(1)).subscribe(() => {
            this.submitAttempted = false;
            this.cdr.detectChanges();
        });
    }

    openEditReceiverDialog(index: 0 | 1) {
        this.clearCurrentEditing();
        this.editingCommIndex = index;
        this.editingReceiver = this.pendingRemoval[index] ? null : { ...this.centralStation.receivers[index] };
        this.cdr.markForCheck();
    }

    private clearCurrentEditing() {
        this.editingCommIndex = null;
        this.editingReceiver = null;
    }

    setReceiver(event, index: 0 | 1) {
        if (event?.removeReceiver && this.canRemoveReceiver()) {
            this.pendingRemoval[index] = event.receiver;
            this.onRemoveReceiverUpdate.emit(this.pendingRemovalValues);
            this.showRemoveReceiverMsg(0);
        } else if (event?.receiver) {
            const { path, lineNum, recvNum } = event.receiver;
            const receivers = [...this.centralStation.receivers];
            if (this.pendingRemoval[index]) {
                receivers[index] = { ...receivers[index], path, lineNum, recvNum };
                delete this.pendingRemoval[index];
                this.onRemoveReceiverUpdate.emit(this.pendingRemovalValues);
            } else {
                receivers[index] = event.receiver;
            }

            this.onReceiverUpdate.emit(receivers);
        }
        this.clearCurrentEditing();
    }

    cancel() {
        this.onCancel.emit();
    }

    viewDeviceList() {
        this.onShowReceiverAccounts.emit(this.centralStation?.id);
    }

    getReceiverStatus(receiver: AlulaReceiver) {
        return this.alulaReceiverService.getReceiverStatus(receiver);
    }

    phoneReceiverChanges() {
        return this.phoneReceiverFields.path.valueChanges.pipe(
            tap(_ => {
                if (this.pendingRemoval[2]) {
                    delete this.pendingRemoval[2];
                    this.onRemoveReceiverUpdate.emit(this.pendingRemovalValues);
                }
            })
        );
    }

    /**
     * handle onBlur event
     */
    blurHandler() {
        AppHelperService.blurPhoneFieldHandler(this.phoneReceiverForm, "path", "(xxx)xxx-xxxx");
    }

    removeStandardPhoneLine() {
        if (this.canRemoveReceiver()) {
            this.pendingRemoval[2] = this.phoneReceiverForm.value;
            this.onRemoveReceiverUpdate.emit(this.pendingRemovalValues);
            this.phoneReceiverFields.path.setValue("", { emitEvent: false });
            this.showRemoveReceiverMsg(1);
        }
    }
    canRemoveReceiver() {
        const availableReceivers = this.centralStation.receivers.filter(
            rcvr => !this.pendingRemovalValues.some(val => rcvr?.id === val.id)
        ).length;
        if (availableReceivers === 1) {
            const receiverRequiredMsg = "This communication path cannot be removed. At least one communication path is required.";

            this.alulaDispatcher.dispatchAction(
                SnackbarActions.openSnackbar({
                    message: new HttpResponseHandler(null, null, receiverRequiredMsg, true),
                })
            );
            return false;
        }
        return true;
    }

    public isCommPathSecureXML(path: "path1Protocol" | "path2Protocol"): boolean {
        return this.centralStation?.[path] === ReceiverTypes.matrix;
    }
}
